import React, { useContext, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'components/templates';
import ToolContext, { ToolContextType } from 'components/services/ToolContext';
import ReducersIndex from 'reduxModules/ducks';
import { RootState } from 'types/redux/state';
import { ModalState } from 'types/redux/ui';
import { ModalManagerProps } from 'types/services/ModalManager';
import ExpansionDialog, { EXPANSION_RECOMMENDATION_MODALS } from 'components/organisms/ExpansionDialog';
import ManualEditResetDialog, { CONFIRM_MANUAL_EDIT_RESET_MODAL } from 'components/organisms/ManualEditResetDialog';

/**
 * Modals owned by dedicated dialog components — filtered out of the generic Modal
 * renderer below so they don't double-render. Module-scoped so the array isn't
 * reallocated on every render of ModalManager.
 */
const OWNED_MODAL_NAMES = [...EXPANSION_RECOMMENDATION_MODALS, CONFIRM_MANUAL_EDIT_RESET_MODAL];

/**
 * ModalManager component handles the display of modals based on Redux state
 *
 * @param props - Component props
 * @returns React component that renders active modals
 */
const ModalManager: React.FC<ModalManagerProps> = ({ top }) => {
  // Get modals from Redux state
  const modals = useSelector<RootState, ModalState[]>(state => state.ui.modals);
  
  // Find the currently open modal (if any)
  const currentModal = modals.find(modal => modal.modalOpen)?.modalName;
  
  // Get the Tool Context and extract the menu for the current modal
  const toolContext = useContext<ToolContextType | undefined>(ToolContext);
  const menu = currentModal && toolContext?.modals[currentModal]?.menu;
  
  // Get dispatch function for Redux actions
  const dispatch = useDispatch();
  
  // Effect to update current view when a modal with a menu is opened
  useEffect(() => {
    if (menu) {
      dispatch(ReducersIndex.uiReducers.setCurrentView(menu));
    }
  }, [menu, dispatch]);
  
  // OWNED_MODAL_NAMES lives at module scope above. ExpansionDialog owns the bar +
  // expansion recommendation flows; ManualEditResetDialog owns the parameter-change confirm.
  const genericModals = modals.filter(m => !OWNED_MODAL_NAMES.includes(m.modalName));

  return (
    <Fragment>
      {genericModals.map((modal, index) => (
        <Modal
          key={`modal-${index}`}
          modalName={modal.modalName}
          top={top}
        />
      ))}
      <ExpansionDialog />
      <ManualEditResetDialog />
    </Fragment>
  );
};

export default ModalManager;
